/**
 * Content Manager Page
 * 마케팅 콘텐츠 자동 생성 관리 페이지
 * - 토픽 선택 및 콘텐츠 생성
 * - 생성된 콘텐츠 목록 관리
 * - 플랫폼별 변환 콘텐츠 보기/복사
 * - 발행 상태 추적
 * 
 * Author: feature/marketing-automation
 * Created: 2026-01-30
 * Updated: 2026-01-31 - 플랫폼별 콘텐츠 변환 UI 추가
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../../firebase/config';
import { checkAdminPermissions } from '../../api/adminService';
import { contentTopics, getTopicsByCategory, getTopicsByPriority } from '../../data/marketing/contentTopics';
import './ContentManager.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// 플랫폼 설정
const PLATFORMS = {
  instagram: { name: '인스타그램', icon: '📸', color: '#E4405F', needsImage: true, tone: '어그로+공감', format: 'text' },
  threads: { name: '스레드', icon: '🧵', color: '#000000', needsImage: false, tone: '어그로+공감', format: 'text' },
  facebook: { name: '페이스북', icon: '📘', color: '#1877F2', needsImage: false, tone: '정보공유', format: 'text' },
  naver_blog: { name: '네이버 블로그', icon: '🟢', color: '#03C75A', needsImage: false, tone: '아티클 복붙', format: 'text', useArticle: true },
  tistory: { name: '티스토리', icon: '🟠', color: '#FF5A00', needsImage: true, tone: 'SEO', format: 'markdown' },
  linkedin: { name: '링크드인', icon: '💼', color: '#0A66C2', needsImage: false, tone: '전문가', format: 'text' },
  velog: { name: 'Velog', icon: '💚', color: '#20C997', needsImage: true, tone: '테크', format: 'markdown' },
  medium: { name: '미디움', icon: '📝', color: '#000000', needsImage: true, tone: '인사이트', format: 'markdown' },
  naver_cafe: { name: '네이버 카페', icon: '☕', color: '#03C75A', needsImage: false, tone: '커뮤니티', format: 'text' },
  kakaotalk: { name: '카카오톡', icon: '💬', color: '#FEE500', needsImage: false, tone: '단톡방', format: 'text' }
};

// 🎨 왓퀴즈 아이덴티티: 하찮지만 귀여운 손그림
const IMAGE_STYLE_TYPES = {
  A: { name: '하찮은 귀여움', icon: '✏️', color: '#FFB5BA', description: '대충 그렸지만 매력적' },
  B: { name: '하찮은 비교짤', icon: '⚡', color: '#B5E8E3', description: '대충 그린 이상vs현실' },
  C: { name: '하찮은 숫자강조', icon: '🔢', color: '#B5D8FF', description: '대충 쓴 큰 숫자' },
  D: { name: '하찮은 메모', icon: '📝', color: '#C5E8B7', description: '급하게 적은 포스트잇' },
  E: { name: '하찮은 꿀팁', icon: '💡', color: '#FFE5B5', description: '대충 적은 꿀팁카드' }
};

const ContentManager = () => {
  const navigate = useNavigate();
  const [user, loading] = useAuthState(auth);
  
  // 상태 관리
  const [isAdmin, setIsAdmin] = useState(false);
  const [activeTab, setActiveTab] = useState('generate'); // generate | list | shorts | settings
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [contentType, setContentType] = useState('blog');
  const [useWebSearch, setUseWebSearch] = useState(true);
  
  // 생성 상태
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedContent, setGeneratedContent] = useState(null);
  const [generateError, setGenerateError] = useState(null);
  
  // 콘텐츠 목록
  const [contentList, setContentList] = useState([]);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [selectedContent, setSelectedContent] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null); // 선택된 날짜
  
  // 플랫폼별 콘텐츠
  const [selectedPlatform, setSelectedPlatform] = useState('original');
  const [platformVersions, setPlatformVersions] = useState({});
  const [copySuccess, setCopySuccess] = useState(null);
  const [htmlPreviewMode, setHtmlPreviewMode] = useState(false); // HTML 미리보기 모드
  
  // 수동 소셜 콘텐츠 생성 (인스타/스레드)
  const [manualSocialTopic, setManualSocialTopic] = useState('');
  const [manualSocialInstructions, setManualSocialInstructions] = useState('');
  const [manualSocialPlatforms, setManualSocialPlatforms] = useState({ instagram: true, threads: true });
  const [manualSocialCardnews, setManualSocialCardnews] = useState(true);
  const [manualSocialSlideCount, setManualSocialSlideCount] = useState(5);
  const [isGeneratingManualSocial, setIsGeneratingManualSocial] = useState(false);
  const [manualSocialResult, setManualSocialResult] = useState(null);
  const [manualSocialError, setManualSocialError] = useState(null);
  
  // 유튜브 쇼츠
  const [shortsList, setShortsList] = useState([]);
  const [isLoadingShorts, setIsLoadingShorts] = useState(false);
  const [selectedShorts, setSelectedShorts] = useState(null);
  
  // 날짜별 그룹화 함수
  const groupByDate = (contents) => {
    const groups = {};
    contents.forEach(content => {
      const date = new Date(content.generated_at).toLocaleDateString('ko-KR');
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(content);
    });
    return groups;
  };
  
  // 날짜별로 그룹화된 콘텐츠
  const contentByDate = groupByDate(contentList);
  const dates = Object.keys(contentByDate).sort((a, b) => new Date(b) - new Date(a));
  
  // 선택된 날짜의 콘텐츠
  const selectedDateContents = selectedDate ? contentByDate[selectedDate] || [] : [];
  
  // 카테고리별 토픽
  const topicsByCategory = getTopicsByCategory();
  const categories = ['all', ...Object.keys(topicsByCategory)];
  
  // 필터링된 토픽
  const filteredTopics = selectedCategory === 'all' 
    ? getTopicsByPriority(3) 
    : topicsByCategory[selectedCategory] || [];

  // 권한 확인
  useEffect(() => {
    const checkPermissions = async () => {
      if (!user) return;
      try {
        const permissions = await checkAdminPermissions();
        setIsAdmin(permissions.is_admin);
        if (!permissions.is_admin) {
          navigate('/');
        }
      } catch (error) {
        console.error('권한 확인 실패:', error);
        navigate('/');
      }
    };
    
    if (!loading && user) {
      checkPermissions();
    } else if (!loading && !user) {
      navigate('/auth');
    }
  }, [user, loading, navigate]);

  // 콘텐츠 목록 로드 (더 많은 콘텐츠 표시)
  const [listLimit, setListLimit] = useState(100);
  
  const loadContentList = useCallback(async (limit = listLimit) => {
    if (!user) return;
    
    setIsLoadingList(true);
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${API_BASE_URL}/content/list?limit=${limit}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setContentList(data.contents || []);
      }
    } catch (error) {
      console.error('콘텐츠 목록 로드 실패:', error);
    } finally {
      setIsLoadingList(false);
    }
  }, [user, listLimit]);

  useEffect(() => {
    if (activeTab === 'list' && isAdmin) {
      loadContentList();
    }
  }, [activeTab, isAdmin, loadContentList]);

  // 쇼츠 목록 로드
  const loadShortsList = useCallback(async () => {
    if (!user) return;
    
    setIsLoadingShorts(true);
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${API_BASE_URL}/content/youtube-shorts?_t=${Date.now()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setShortsList(data.shorts || []);
      }
    } catch (error) {
      console.error('쇼츠 목록 로드 실패:', error);
    } finally {
      setIsLoadingShorts(false);
    }
  }, [user]);

  useEffect(() => {
    if (activeTab === 'shorts' && isAdmin) {
      loadShortsList();
    }
  }, [activeTab, isAdmin, loadShortsList]);

  // 콘텐츠 생성
  const handleGenerate = async () => {
    if (!selectedTopic || !user) return;
    
    setIsGenerating(true);
    setGenerateError(null);
    setGeneratedContent(null);
    
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${API_BASE_URL}/content/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          topic: selectedTopic,
          content_type: contentType,
          use_web_search: useWebSearch,
          save_to_db: true
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setGeneratedContent(data);
      } else {
        setGenerateError(data.error || '콘텐츠 생성에 실패했습니다');
      }
    } catch (error) {
      console.error('콘텐츠 생성 실패:', error);
      setGenerateError(error.message || '네트워크 오류가 발생했습니다');
    } finally {
      setIsGenerating(false);
    }
  };

  // 일일 콘텐츠 자동 생성 (v2 - 웹검색 기반 어그로 토픽)
  // 블로그 1개만 생성 (테스트용)
  const handleDailyGenerate = async () => {
    if (!user) return;
    
    setIsGenerating(true);
    setGenerateError(null);
    
    try {
      const token = await user.getIdToken();
      // v2 API 사용: 블로그 1개만, 플랫폼 변환/이미지 없이, auto_publish=true (발행)
      const response = await fetch(
        `${API_BASE_URL}/content/generate-daily-with-platforms?general_count=1&include_realtime=false&auto_publish=true&generate_platforms=false&generate_images=false`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      const data = await response.json();
      
      if (data.success || data.summary) {
        const generalArticles = data.general_articles || [];
        const successCount = generalArticles.filter(a => a.success).length;
        const viralUsed = data.viral_topics_used || 0;
        const dbUsed = data.db_topics_used || 0;
        
        setGeneratedContent({
          content: `📝 블로그 생성 + 발행 완료!\n\n` +
            `✅ 생성: ${successCount}개 블로그\n` +
            `🔥 어그로 토픽: ${viralUsed}개 (웹검색)\n` +
            `📚 DB 토픽: ${dbUsed}개\n` +
            `📌 상태: 발행됨 (IndexNow 인덱싱 요청됨)\n\n` +
            `👉 "생성 목록" 탭 또는 /articles 에서 확인하세요!`,
          metadata: data
        });
        // 목록 새로고침
        loadContentList();
      } else {
        setGenerateError(data.error || '일일 콘텐츠 생성에 실패했습니다');
      }
    } catch (error) {
      console.error('일일 생성 실패:', error);
      setGenerateError(error.message);
    } finally {
      setIsGenerating(false);
    }
  };

  // 🔥 바이럴 콘텐츠 테스트 생성 (블로그 + 플랫폼 변환 + 이미지)
  // 스케줄러와 완전히 동일: 일반 3개 + 실시간 1개 = 총 4개 블로그 + 7개 플랫폼 변환 + 이미지
  const handleViralContentTest = async () => {
    if (!user) return;
    
    setIsGenerating(true);
    setGenerateError(null);
    
    try {
      const token = await user.getIdToken();
      // 🔥 스케줄러와 완전 동일한 설정: 일반 3개 + 실시간 1개 + 7개 플랫폼 + 이미지
      const response = await fetch(
        `${API_BASE_URL}/content/generate-daily-with-platforms?general_count=3&include_realtime=true&auto_publish=true&generate_platforms=true&generate_images=true`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      const data = await response.json();
      
      if (data.success || data.summary) {
        const summary = data.summary || {};
        setGeneratedContent({
          content: `🔥 바이럴 콘텐츠 생성 완료!\n\n` +
            `📝 블로그: ${summary.contents_generated || 4}개 생성 (일반 3개 + 실시간 1개)\n` +
            `🎯 플랫폼 변환: ${summary.platforms_converted || 0}개 (7개 플랫폼)\n` +
            `🖼️ 이미지 생성: ${summary.images_generated || 0}개\n\n` +
            `👉 "생성 목록" 탭에서 결과 확인하세요!`,
          metadata: data
        });
        // 목록 새로고침
        loadContentList();
      } else {
        setGenerateError(data.error || '바이럴 콘텐츠 생성에 실패했습니다');
      }
    } catch (error) {
      console.error('바이럴 테스트 실패:', error);
      setGenerateError(error.message);
    } finally {
      setIsGenerating(false);
    }
  };

  // 📸🧵 수동 인스타/스레드 콘텐츠 생성
  const handleManualSocialGenerate = async () => {
    if (!user || !manualSocialTopic.trim()) return;
    
    setIsGeneratingManualSocial(true);
    setManualSocialError(null);
    setManualSocialResult(null);
    
    try {
      const token = await user.getIdToken();
      const selectedPlatformsList = Object.entries(manualSocialPlatforms)
        .filter(([_, checked]) => checked)
        .map(([platform]) => platform);
      
      if (selectedPlatformsList.length === 0) {
        setManualSocialError('최소 1개 플랫폼을 선택해주세요');
        setIsGeneratingManualSocial(false);
        return;
      }
      
      const response = await fetch(`${API_BASE_URL}/content/generate-manual-social`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          topic: manualSocialTopic.trim(),
          custom_instructions: manualSocialInstructions.trim(),
          platforms: selectedPlatformsList,
          generate_cardnews: manualSocialCardnews && selectedPlatformsList.includes('instagram'),
          slide_count: manualSocialSlideCount
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setManualSocialResult(data);
      } else {
        setManualSocialError(data.detail || data.error || '소셜 콘텐츠 생성에 실패했습니다');
      }
    } catch (error) {
      console.error('수동 소셜 생성 실패:', error);
      setManualSocialError(error.message || '네트워크 오류가 발생했습니다');
    } finally {
      setIsGeneratingManualSocial(false);
    }
  };

  // 콘텐츠 상세 보기
  const handleViewContent = async (contentId) => {
    if (!user) return;
    
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${API_BASE_URL}/content/${contentId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSelectedContent(data.content);
        
        // 플랫폼 버전 데이터 로드 (상세 정보 포함)
        // 목록에서 가져온 데이터는 간략해서 항상 상세 API 호출
        if (data.content?.platform_versions && Object.values(data.content.platform_versions).some(v => v?.content)) {
          // 상세 정보가 포함되어 있으면 바로 사용
          setPlatformVersions(data.content.platform_versions);
        } else {
          // 간략 정보만 있으면 별도 API 호출
          loadPlatformVersions(contentId);
        }
      }
    } catch (error) {
      console.error('콘텐츠 상세 로드 실패:', error);
    }
  };

  // 발행 완료 표시
  const handleMarkPublished = async (contentId, platform) => {
    if (!user) return;
    
    try {
      const token = await user.getIdToken();
      await fetch(`${API_BASE_URL}/content/${contentId}/publish?platform=${platform}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      loadContentList();
      if (selectedContent?.id === contentId) {
        handleViewContent(contentId);
      }
    } catch (error) {
      console.error('발행 표시 실패:', error);
    }
  };

  // 플랫폼별 버전 조회
  const loadPlatformVersions = useCallback(async (contentId) => {
    if (!user || !contentId) return;
    
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${API_BASE_URL}/content/${contentId}/platforms`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPlatformVersions(data.platform_versions || {});
      }
    } catch (error) {
      console.error('플랫폼 버전 조회 실패:', error);
    }
  }, [user]);

  // 콘텐츠 복사
  const handleCopyContent = async (content) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(null), 2000);
    } catch (error) {
      console.error('복사 실패:', error);
    }
  };

  // 이미지 다운로드
  const handleDownloadImage = (base64Data, mimeType, platform) => {
    const link = document.createElement('a');
    link.href = `data:${mimeType};base64,${base64Data}`;
    link.download = `whatquiz-${platform}-${Date.now()}.png`;
    link.click();
  };

  // 콘텐츠에서 해시태그 추출
  const extractHashtags = (content) => {
    if (!content) return null;
    // #으로 시작하는 해시태그 추출
    const hashtagMatch = content.match(/#[\w가-힣]+/g);
    if (hashtagMatch && hashtagMatch.length > 0) {
      return hashtagMatch.join(' ');
    }
    // [해시태그] 섹션 추출
    const sectionMatch = content.match(/\[해시태그\]\s*([\s\S]*?)(?:\n\n|```|$)/i);
    if (sectionMatch) {
      return sectionMatch[1].trim();
    }
    return null;
  };

  // 콘텐츠에서 본문만 추출 (해시태그, 댓글 섹션 제거)
  const extractMainContent = (content) => {
    if (!content) return '';
    let clean = content;
    // 마지막 해시태그 섹션 제거 (본문은 유지)
    clean = clean.replace(/\n*\[해시태그\][\s\S]*$/i, '');
    clean = clean.replace(/\n*---\s*\n*#[\w가-힣\s#]+$/i, ''); // ---로 구분된 해시태그
    // 댓글용 섹션 제거
    clean = clean.replace(/\n*---\s*\n*\[댓글용\][\s\S]*$/i, '');
    clean = clean.replace(/\n*\[댓글용\][\s\S]*$/i, '');
    return clean.trim();
  };

  // 콘텐츠에서 댓글용 섹션 추출
  const extractCommentSection = (content) => {
    if (!content) return null;
    // [댓글용] 섹션 추출
    const commentMatch = content.match(/\[댓글용\]\s*([\s\S]*?)(?:\n\n\[|```|$)/i);
    if (commentMatch) {
      return commentMatch[1].trim();
    }
    // ---로 구분된 댓글용 섹션
    const dashMatch = content.match(/---\s*\n*\[댓글용\]\s*([\s\S]*?)$/i);
    if (dashMatch) {
      return dashMatch[1].trim();
    }
    return null;
  };

  // HTML 콘텐츠 추출 (네이버 블로그용)
  const extractHtmlContent = (content) => {
    if (!content) return '';
    // [HTML 본문] 섹션 추출
    const htmlMatch = content.match(/\[HTML 본문\]\s*([\s\S]*?)(?:\n---|\[댓글용\]|$)/i);
    if (htmlMatch) {
      return htmlMatch[1].trim();
    }
    // 코드블럭 없는 HTML 추출 시도
    const mainContent = extractMainContent(content);
    // 제목 섹션 제거
    const withoutTitle = mainContent.replace(/\[제목\][\s\S]*?---/i, '').trim();
    return withoutTitle || mainContent;
  };

  // 콘텐츠 선택 시 플랫폼 버전도 로드 (플랫폼 탭은 유지)
  useEffect(() => {
    if (selectedContent?.id) {
      loadPlatformVersions(selectedContent.id);
      // 주의: selectedPlatform을 'original'로 리셋하지 않음
      // 플랫폼 목록에서 콘텐츠 선택 시 현재 탭 유지
    }
  }, [selectedContent?.id, loadPlatformVersions]);

  if (loading) {
    return <div className="content-manager-loading">로딩 중...</div>;
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="content-manager-page">
      <Helmet>
        <title>콘텐츠 자동화 관리 | 왓퀴즈 Admin</title>
      </Helmet>

      <div className="content-manager-container">
        {/* 헤더 */}
        <header className="content-manager-header">
          <h1 className="content-manager-title">콘텐츠 자동화 관리</h1>
          <p className="content-manager-subtitle">
            Gemini 3 Pro 기반 마케팅 콘텐츠 자동 생성 시스템
          </p>
        </header>

        {/* 탭 네비게이션 */}
        <nav className="content-manager-tabs">
          <button 
            className={`content-manager-tab ${activeTab === 'generate' ? 'active' : ''}`}
            onClick={() => setActiveTab('generate')}
          >
            콘텐츠 생성
          </button>
          <button 
            className={`content-manager-tab ${activeTab === 'list' ? 'active' : ''}`}
            onClick={() => setActiveTab('list')}
          >
            생성 목록
          </button>
          <button 
            className={`content-manager-tab ${activeTab === 'shorts' ? 'active' : ''}`}
            onClick={() => setActiveTab('shorts')}
          >
            🎬 유튜브 쇼츠
          </button>
          <button 
            className={`content-manager-tab ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            설정
          </button>
        </nav>

        {/* 탭 콘텐츠 */}
        <div className="content-manager-content">
          {/* 생성 탭 */}
          {activeTab === 'generate' && (
            <div className="content-manager-generate">
              {/* 빠른 생성 버튼 */}
              <div className="content-manager-quick-actions">
                <button 
                  className="content-manager-daily-btn"
                  onClick={handleDailyGenerate}
                  disabled={isGenerating}
                  title="블로그 1개 생성 + 발행 (웹검색 기반 어그로 토픽)"
                >
                  {isGenerating ? '생성 중... (최대 5분)' : '📝 블로그 1개 생성'}
                </button>
                <button 
                  className="content-manager-viral-btn"
                  onClick={handleViralContentTest}
                  disabled={isGenerating}
                  title="스케줄러와 동일: 블로그 4개 + 7개 플랫폼 변환 + 이미지 (실제 발행됨!)"
                >
                  {isGenerating ? '생성 중... (최대 10분)' : '🔥 실제 발행 (스케줄러 동일)'}
                </button>
              </div>

              {/* 📸🧵 수동 인스타/스레드 생성 섹션 */}
              <div className="content-manager-manual-social">
                <h3>📸🧵 인스타/스레드 수동 생성</h3>
                <p className="content-manager-manual-social-desc">
                  원하는 주제로 인스타그램/스레드 콘텐츠를 직접 만들 수 있어요. 스케줄러와 동일한 AI(gemini-3-pro, 최대 토큰)를 사용합니다.
                </p>
                
                <div className="content-manager-manual-social-form">
                  {/* 주제 입력 */}
                  <div className="content-manager-manual-social-field">
                    <label>주제 *</label>
                    <input
                      type="text"
                      value={manualSocialTopic}
                      onChange={(e) => setManualSocialTopic(e.target.value)}
                      placeholder="예: 설날 연휴 이벤트, 새학기 준비, 봄방학 꿀팁..."
                      disabled={isGeneratingManualSocial}
                    />
                  </div>
                  
                  {/* 추가 지시사항 */}
                  <div className="content-manager-manual-social-field">
                    <label>추가 지시사항 (선택)</label>
                    <textarea
                      value={manualSocialInstructions}
                      onChange={(e) => setManualSocialInstructions(e.target.value)}
                      placeholder="예: 팔로우해주시는 분들께 감사 인사 + 무료 쿠폰 DM 이벤트 + 올해 응원 메시지"
                      rows={3}
                      disabled={isGeneratingManualSocial}
                    />
                  </div>
                  
                  {/* 플랫폼 선택 + 옵션 */}
                  <div className="content-manager-manual-social-options">
                    <div className="content-manager-manual-social-platforms">
                      <label>플랫폼</label>
                      <div className="content-manager-manual-social-checkboxes">
                        <label className="content-manager-checkbox-label">
                          <input
                            type="checkbox"
                            checked={manualSocialPlatforms.instagram}
                            onChange={(e) => setManualSocialPlatforms(prev => ({ ...prev, instagram: e.target.checked }))}
                            disabled={isGeneratingManualSocial}
                          />
                          📸 인스타그램
                        </label>
                        <label className="content-manager-checkbox-label">
                          <input
                            type="checkbox"
                            checked={manualSocialPlatforms.threads}
                            onChange={(e) => setManualSocialPlatforms(prev => ({ ...prev, threads: e.target.checked }))}
                            disabled={isGeneratingManualSocial}
                          />
                          🧵 스레드
                        </label>
                      </div>
                    </div>
                    
                    {manualSocialPlatforms.instagram && (
                      <div className="content-manager-manual-social-cardnews-options">
                        <label className="content-manager-checkbox-label">
                          <input
                            type="checkbox"
                            checked={manualSocialCardnews}
                            onChange={(e) => setManualSocialCardnews(e.target.checked)}
                            disabled={isGeneratingManualSocial}
                          />
                          🎴 카드뉴스 생성
                        </label>
                        {manualSocialCardnews && (
                          <select
                            value={manualSocialSlideCount}
                            onChange={(e) => setManualSocialSlideCount(Number(e.target.value))}
                            disabled={isGeneratingManualSocial}
                            className="content-manager-slide-count-select"
                          >
                            <option value={2}>2장</option>
                            <option value={3}>3장</option>
                            <option value={4}>4장</option>
                            <option value={5}>5장</option>
                          </select>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* 생성 버튼 */}
                  <button
                    className="content-manager-manual-social-btn"
                    onClick={handleManualSocialGenerate}
                    disabled={isGeneratingManualSocial || !manualSocialTopic.trim()}
                  >
                    {isGeneratingManualSocial 
                      ? '📸🧵 생성 중... (최대 10분, 카드뉴스 포함 시 더 걸릴 수 있어요)' 
                      : '📸🧵 인스타/스레드 만들기'}
                  </button>
                  
                  {/* 에러 표시 */}
                  {manualSocialError && (
                    <div className="content-manager-error">
                      ❌ {manualSocialError}
                    </div>
                  )}
                </div>
                
                {/* 결과 프리뷰 */}
                {manualSocialResult && (
                  <div className="content-manager-manual-social-result">
                    <h4>✅ 생성 완료! ({manualSocialResult.summary})</h4>
                    
                    {/* 인스타그램 결과 */}
                    {manualSocialResult.platforms?.instagram?.success && (
                      <div className="content-manager-manual-social-platform-result">
                        <div className="content-manager-manual-social-platform-header">
                          <span>📸 인스타그램</span>
                          <button
                            className="content-manager-copy-btn"
                            onClick={() => {
                              navigator.clipboard.writeText(manualSocialResult.platforms.instagram.content);
                              setCopySuccess('instagram-manual');
                              setTimeout(() => setCopySuccess(null), 2000);
                            }}
                          >
                            {copySuccess === 'instagram-manual' ? '✅ 복사됨!' : '📋 복사'}
                          </button>
                        </div>
                        <pre className="content-manager-manual-social-content">
                          {manualSocialResult.platforms.instagram.content}
                        </pre>
                        
                        {/* 카드뉴스 이미지 */}
                        {manualSocialResult.cardnews && manualSocialResult.cardnews.length > 0 && (
                          <div className="content-manager-manual-social-cardnews">
                            <h5>🎴 카드뉴스 ({manualSocialResult.cardnews.length}장)</h5>
                            <div className="content-manager-manual-social-cardnews-grid">
                              {manualSocialResult.cardnews.map((slide, idx) => (
                                <div key={idx} className="content-manager-manual-social-slide">
                                  {slide.url ? (
                                    <img src={slide.url} alt={slide.title || `슬라이드 ${idx + 1}`} />
                                  ) : (
                                    <div className="content-manager-manual-social-slide-placeholder">
                                      {slide.title || `슬라이드 ${idx + 1}`}
                                    </div>
                                  )}
                                  <span className="content-manager-manual-social-slide-label">
                                    {slide.type === 'hook' ? '🎯 훅' : slide.type === 'cta' ? '📢 CTA' : `📄 ${idx + 1}장`}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* 스레드 결과 */}
                    {manualSocialResult.platforms?.threads?.success && (
                      <div className="content-manager-manual-social-platform-result">
                        <div className="content-manager-manual-social-platform-header">
                          <span>🧵 스레드</span>
                          <button
                            className="content-manager-copy-btn"
                            onClick={() => {
                              navigator.clipboard.writeText(manualSocialResult.platforms.threads.content);
                              setCopySuccess('threads-manual');
                              setTimeout(() => setCopySuccess(null), 2000);
                            }}
                          >
                            {copySuccess === 'threads-manual' ? '✅ 복사됨!' : '📋 복사'}
                          </button>
                        </div>
                        <pre className="content-manager-manual-social-content">
                          {manualSocialResult.platforms.threads.content}
                        </pre>
                      </div>
                    )}
                    
                    {/* 실패한 플랫폼 */}
                    {Object.entries(manualSocialResult.platforms || {})
                      .filter(([_, r]) => !r.success)
                      .map(([platform, result]) => (
                        <div key={platform} className="content-manager-manual-social-platform-error">
                          ❌ {PLATFORMS[platform]?.name || platform}: {result.error}
                        </div>
                      ))
                    }
                  </div>
                )}
              </div>

              <div className="content-manager-generate-grid">
                {/* 토픽 선택 */}
                <div className="content-manager-topic-selector">
                  <h3>토픽 선택</h3>
                  
                  {/* 카테고리 필터 */}
                  <div className="content-manager-category-filter">
                    {categories.map(cat => (
                      <button
                        key={cat}
                        className={`content-manager-category-btn ${selectedCategory === cat ? 'active' : ''}`}
                        onClick={() => setSelectedCategory(cat)}
                      >
                        {cat === 'all' ? '전체' : cat}
                      </button>
                    ))}
                  </div>

                  {/* 토픽 목록 */}
                  <div className="content-manager-topic-list">
                    {filteredTopics.slice(0, 30).map(topic => (
                      <div
                        key={topic.id}
                        className={`content-manager-topic-item ${selectedTopic?.id === topic.id ? 'selected' : ''}`}
                        onClick={() => setSelectedTopic(topic)}
                      >
                        <span className="content-manager-topic-priority">P{topic.priority}</span>
                        <span className="content-manager-topic-name">{topic.topic}</span>
                        <span className="content-manager-topic-category">{topic.category}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* 생성 옵션 */}
                <div className="content-manager-options">
                  <h3>생성 옵션</h3>
                  
                  {selectedTopic && (
                    <div className="content-manager-selected-topic">
                      <strong>선택된 토픽:</strong>
                      <p>{selectedTopic.topic}</p>
                      <div className="content-manager-topic-keywords">
                        {selectedTopic.keywords.map(kw => (
                          <span key={kw} className="content-manager-keyword">{kw}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="content-manager-option-group">
                    <label>콘텐츠 유형</label>
                    <select 
                      value={contentType} 
                      onChange={(e) => setContentType(e.target.value)}
                    >
                      <option value="blog">블로그 포스트</option>
                      <option value="sns">SNS 포스트</option>
                    </select>
                  </div>

                  <div className="content-manager-option-group">
                    <label className="content-manager-checkbox">
                      <input
                        type="checkbox"
                        checked={useWebSearch}
                        onChange={(e) => setUseWebSearch(e.target.checked)}
                      />
                      웹검색 활용 (최신 정보)
                    </label>
                  </div>

                  <button
                    className="content-manager-generate-btn"
                    onClick={handleGenerate}
                    disabled={!selectedTopic || isGenerating}
                  >
                    {isGenerating ? '생성 중... (최대 5분)' : '콘텐츠 생성'}
                  </button>

                  {generateError && (
                    <div className="content-manager-error">
                      {generateError}
                    </div>
                  )}
                </div>
              </div>

              {/* 생성 결과 */}
              {generatedContent && (
                <div className="content-manager-result">
                  <h3>생성 결과</h3>
                  {generatedContent.metadata && (
                    <div className="content-manager-metadata">
                      <p><strong>SEO 제목:</strong> {generatedContent.metadata.seoTitle}</p>
                      <p><strong>메타 설명:</strong> {generatedContent.metadata.metaDescription}</p>
                      {generatedContent.metadata.hashtags && (
                        <div className="content-manager-hashtags">
                          {generatedContent.metadata.hashtags.map(tag => (
                            <span key={tag}>#{tag}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  <div className="content-manager-preview">
                    <pre>{generatedContent.content}</pre>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 목록 탭 - 날짜별 대시보드 */}
          {activeTab === 'list' && (
            <div className="content-manager-list">
              <div className="content-manager-list-header">
                <h3>📅 날짜별 콘텐츠 ({dates.length}일, 총 {contentList.length}개)</h3>
                <div className="content-manager-list-actions">
                  <select 
                    value={listLimit} 
                    onChange={(e) => {
                      const newLimit = parseInt(e.target.value);
                      setListLimit(newLimit);
                      loadContentList(newLimit);
                    }}
                    className="content-manager-limit-select"
                  >
                    <option value={50}>최근 50개</option>
                    <option value={100}>최근 100개</option>
                    <option value={200}>최근 200개</option>
                    <option value={500}>전체 보기</option>
                  </select>
                  <button onClick={() => loadContentList(listLimit)} disabled={isLoadingList}>
                    {isLoadingList ? '로딩...' : '🔄 새로고침'}
                  </button>
                </div>
              </div>

              {/* 복사 알림 토스트 */}
              {copySuccess && (
                <div className="content-manager-copy-toast">
                  복사했어요
                </div>
              )}

              <div className="content-manager-list-grid">
                {/* 날짜 목록 (왼쪽) */}
                <div className="content-manager-list-items">
                  {dates.map(date => {
                    const dayContents = contentByDate[date];
                    const platformCount = dayContents.reduce((acc, c) => 
                      acc + (c.platform_versions ? Object.values(c.platform_versions).filter(v => v?.success).length : 0), 0);
                    return (
                      <div 
                        key={date} 
                        className={`content-manager-list-item ${selectedDate === date ? 'selected' : ''}`}
                        onClick={() => {
                          setSelectedDate(date);
                          setSelectedContent(null);
                          setSelectedPlatform('original');
                        }}
                      >
                        <div className="content-manager-list-item-header">
                          <span className="content-manager-list-item-type">
                            📅 {date}
                          </span>
                        </div>
                        <p className="content-manager-list-item-preview">
                          📝 블로그 {dayContents.length}개 발행
                        </p>
                        <div className="content-manager-list-item-status">
                          {platformCount > 0 && (
                            <span className="platforms-ready">
                              🎯 {platformCount}개 플랫폼 콘텐츠 준비
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* 날짜 선택 시 상세 보기 */}
                {selectedDate && (
                  <div className="content-manager-detail">
                    <div className="content-manager-detail-header">
                      <h4>📅 {selectedDate} 콘텐츠</h4>
                    </div>
                    
                    {/* 플랫폼 탭 - 가로 스크롤 */}
                    <div className="content-manager-platform-tabs-wrapper">
                      <div className="content-manager-platform-tabs">
                        <button
                          className={`content-manager-platform-tab original ${selectedPlatform === 'original' ? 'active' : ''}`}
                          onClick={() => { setSelectedPlatform('original'); setSelectedContent(null); }}
                        >
                          📝 블로그 원본 ({selectedDateContents.length})
                        </button>
                        {Object.entries(PLATFORMS).map(([key, config]) => {
                          // 이 날짜의 콘텐츠 중 해당 플랫폼 버전이 있는 것들 개수
                          const platformContents = selectedDateContents.filter(c => c.platform_versions?.[key]?.success);
                          const count = platformContents.length;
                          return (
                            <button
                              key={key}
                              className={`content-manager-platform-tab ${selectedPlatform === key ? 'active' : ''} ${count > 0 ? 'has-content' : 'no-content'}`}
                              onClick={() => {
                                setSelectedPlatform(key);
                                setSelectedContent(null); // 목록에서 선택하도록
                              }}
                              style={{ 
                                borderColor: selectedPlatform === key ? config.color : 'transparent',
                                backgroundColor: selectedPlatform === key ? `${config.color}15` : 'transparent'
                              }}
                            >
                              {config.icon} {config.name} {count > 0 && `(${count})`}
                              {count > 0 && <span className="content-manager-platform-check">✓</span>}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    
                    {/* 블로그 원본 선택 시 - 목록 표시 */}
                    {selectedPlatform === 'original' && (
                      <div className="content-manager-blog-list">
                        <p className="content-manager-blog-list-desc">📝 이 날 발행된 블로그 {selectedDateContents.length}개</p>
                        {selectedDateContents.map((content, idx) => (
                          <div 
                            key={content.id} 
                            className={`content-manager-blog-item ${selectedContent?.id === content.id ? 'selected' : ''}`}
                            onClick={() => handleViewContent(content.id)}
                          >
                            <span className="content-manager-blog-item-num">{idx + 1}</span>
                            <span className="content-manager-blog-item-title">
                              {content.content_type === 'manual_social' && <span style={{ color: '#E4405F', fontWeight: 700, marginRight: 4 }}>📸🧵</span>}
                              {content.preview}
                            </span>
                            <span className="content-manager-blog-item-chars">{content.word_count || '?'}자</span>
                          </div>
                        ))}
                        {selectedContent && (
                          <div className="content-manager-blog-preview">
                            <div className="content-manager-content-actions">
                              <button onClick={() => handleCopyContent(selectedContent.content)}>📋 전체 복사</button>
                            </div>
                            <pre className="content-manager-content-text">{selectedContent.content}</pre>
                          </div>
                        )}
                      </div>
                    )}

                    {/* 선택된 플랫폼 콘텐츠 - 해당 플랫폼의 모든 콘텐츠 목록 표시 */}
                    {selectedPlatform !== 'original' && (() => {
                      // 네이버 블로그: 아티클 복붙 안내
                      if (PLATFORMS[selectedPlatform]?.useArticle) {
                        return (
                          <div className="content-manager-no-platform" style={{ textAlign: 'center', padding: '40px 20px' }}>
                            <p style={{ fontSize: '16px', marginBottom: '8px' }}>🟢 네이버 블로그는 왓퀴즈 아티클을 복붙하는 게 더 효과적이에요!</p>
                            <p style={{ fontSize: '14px', color: '#888', marginBottom: '16px' }}>아래 버튼을 눌러 오늘 발행된 아티클을 확인하세요.</p>
                            <a 
                              href="/articles" 
                              target="_blank" 
                              rel="noopener noreferrer"
                              style={{ 
                                display: 'inline-block', padding: '12px 24px', 
                                backgroundColor: '#03C75A', color: '#fff', 
                                borderRadius: '8px', textDecoration: 'none', fontWeight: 600 
                              }}
                            >
                              📰 왓퀴즈 아티클 보러 가기
                            </a>
                          </div>
                        );
                      }

                      // 이 날짜에서 해당 플랫폼 버전이 있는 모든 콘텐츠
                      const platformContents = selectedDateContents.filter(c => c.platform_versions?.[selectedPlatform]?.success);
                      
                      if (platformContents.length === 0) {
                        return (
                          <div className="content-manager-no-platform">
                            <p>😢 이 날짜에 {PLATFORMS[selectedPlatform]?.name} 콘텐츠가 없습니다.</p>
                          </div>
                        );
                      }
                      
                      return (
                        <div className="content-manager-platform-list">
                          {/* 플랫폼 콘텐츠 목록 */}
                          <p className="content-manager-platform-list-desc">
                            {PLATFORMS[selectedPlatform]?.icon} 이 날 생성된 {PLATFORMS[selectedPlatform]?.name} 콘텐츠 <strong>{platformContents.length}개</strong>
                          </p>
                          <div className="content-manager-platform-items">
                            {platformContents.map((content, idx) => (
                              <div 
                                key={content.id} 
                                className={`content-manager-platform-item ${selectedContent?.id === content.id ? 'selected' : ''}`}
                                onClick={() => handleViewContent(content.id)}
                              >
                                <span className="content-manager-platform-item-num">{idx + 1}</span>
                                <span className="content-manager-platform-item-title">
                                  {content.platform_versions?.[selectedPlatform]?.content?.slice(0, 50) || content.preview}...
                                </span>
                              </div>
                            ))}
                          </div>
                          
                          {/* 선택된 콘텐츠 상세 보기 */}
                          {selectedContent && (platformVersions[selectedPlatform]?.success || platformVersions[selectedPlatform]?.content) && (
                            <div className="content-manager-content-view platform">
                              {/* 플랫폼 헤더 (톤 정보 포함) */}
                              <div className="content-manager-platform-header" style={{ backgroundColor: `${PLATFORMS[selectedPlatform]?.color}15` }}>
                                <span className="content-manager-platform-badge" style={{ backgroundColor: PLATFORMS[selectedPlatform]?.color }}>
                                  {PLATFORMS[selectedPlatform]?.icon} {PLATFORMS[selectedPlatform]?.name}
                                </span>
                                {/* 🎯 톤 뱃지 */}
                                <span className="content-manager-tone-badge">
                                  🎯 {PLATFORMS[selectedPlatform]?.tone}
                                </span>
                              </div>

                              {/* 🎴 카드뉴스 섹션 (새로운 통합 방식) */}
                              {platformVersions[selectedPlatform]?.cardnews?.slides?.length > 0 && (
                                <div className="content-manager-copy-section">
                                  <div className="content-manager-copy-section-header">
                                    <span className="content-manager-copy-section-title">
                                      🎴 카드뉴스 ({platformVersions[selectedPlatform].cardnews.slides.length}장)
                                    </span>
                                    <span className="content-manager-cardnews-source">
                                      출처: {platformVersions[selectedPlatform].cardnews.source_blog_id?.substring(0, 30)}...
                                    </span>
                                  </div>
                                  <div className="content-manager-cardnews-gallery">
                                    {platformVersions[selectedPlatform].cardnews.slides.map((slide, idx) => (
                                      <div key={idx} className="content-manager-cardnews-slide">
                                        <div className="content-manager-cardnews-slide-header">
                                          <span className={`content-manager-cardnews-type content-manager-cardnews-type-${slide.type}`}>
                                            {slide.type === 'hook' ? '🔥 훅' : slide.type === 'cta' ? '🎯 CTA' : `📄 ${slide.index}`}
                                          </span>
                                          {slide.url && (
                                            <a 
                                              href={slide.url}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="content-manager-cardnews-download"
                                            >
                                              📥
                                            </a>
                                          )}
                                        </div>
                                        <div className="content-manager-cardnews-slide-content">
                                          <p className="content-manager-cardnews-title">{slide.title}</p>
                                          {slide.body && <p className="content-manager-cardnews-body">{slide.body}</p>}
                                        </div>
                                        {slide.url && (
                                          <div className="content-manager-cardnews-image">
                                            <img src={slide.url} alt={`슬라이드 ${slide.index}`} />
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* 🖼️ 기존 이미지 섹션 (카드뉴스가 없을 때 폴백) */}
                              {!platformVersions[selectedPlatform]?.cardnews?.slides?.length && platformVersions[selectedPlatform]?.image && (
                                <div className="content-manager-copy-section">
                                  <div className="content-manager-copy-section-header">
                                    <span className="content-manager-copy-section-title">🖼️ 이미지</span>
                                    {/* 🎨 바이럴 스타일 타입 뱃지 */}
                                    {platformVersions[selectedPlatform].image.style_type && (
                                      <span 
                                        className="content-manager-style-badge"
                                        style={{ 
                                          backgroundColor: IMAGE_STYLE_TYPES[platformVersions[selectedPlatform].image.style_type]?.color || '#888'
                                        }}
                                        title={IMAGE_STYLE_TYPES[platformVersions[selectedPlatform].image.style_type]?.description}
                                      >
                                        {IMAGE_STYLE_TYPES[platformVersions[selectedPlatform].image.style_type]?.icon} 
                                        스타일 {platformVersions[selectedPlatform].image.style_type}: {IMAGE_STYLE_TYPES[platformVersions[selectedPlatform].image.style_type]?.name}
                                      </span>
                                    )}
                                    {/* URL이 있으면 새 탭에서 열기, base64만 있으면 다운로드 */}
                                    {platformVersions[selectedPlatform].image.url ? (
                                      <a 
                                        href={platformVersions[selectedPlatform].image.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="content-manager-copy-section-btn"
                                      >
                                        📥 새 탭에서 열기
                                      </a>
                                    ) : platformVersions[selectedPlatform].image.base64 && (
                                      <button 
                                        className="content-manager-copy-section-btn"
                                        onClick={() => handleDownloadImage(
                                          platformVersions[selectedPlatform].image.base64,
                                          platformVersions[selectedPlatform].image.mime_type,
                                          selectedPlatform
                                        )}
                                      >
                                        📥 다운로드
                                      </button>
                                    )}
                                  </div>
                                  {/* 핵심 메시지 표시 */}
                                  {platformVersions[selectedPlatform].image.core_message && (
                                    <div className="content-manager-core-message">
                                      💬 핵심 메시지: "{platformVersions[selectedPlatform].image.core_message}"
                                    </div>
                                  )}
                                  <div className="content-manager-image-preview">
                                    <img 
                                      src={
                                        platformVersions[selectedPlatform].image.url 
                                          ? platformVersions[selectedPlatform].image.url
                                          : `data:${platformVersions[selectedPlatform].image.mime_type};base64,${platformVersions[selectedPlatform].image.base64}`
                                      }
                                      alt={`${PLATFORMS[selectedPlatform]?.name} 이미지`}
                                    />
                                  </div>
                                </div>
                              )}

                              {/* 📝 본문 섹션 */}
                              <div className="content-manager-copy-section">
                                <div className="content-manager-copy-section-header">
                                  <span className="content-manager-copy-section-title">📝 본문</span>
                                  <div className="content-manager-copy-section-actions">
                                    {/* HTML 미리보기 토글 (네이버 블로그용) */}
                                    {PLATFORMS[selectedPlatform]?.format === 'html' && (
                                      <button 
                                        className={`content-manager-toggle-btn ${htmlPreviewMode ? 'active' : ''}`}
                                        onClick={() => setHtmlPreviewMode(!htmlPreviewMode)}
                                      >
                                        {htmlPreviewMode ? '📄 코드 보기' : '👁️ 미리보기'}
                                      </button>
                                    )}
                                    <button 
                                      className="content-manager-copy-section-btn primary"
                                      onClick={() => handleCopyContent(
                                        PLATFORMS[selectedPlatform]?.format === 'html' 
                                          ? extractHtmlContent(platformVersions[selectedPlatform]?.content)
                                          : extractMainContent(platformVersions[selectedPlatform]?.content)
                                      )}
                                    >
                                      {copySuccess ? '✅ 복사됨!' : '📋 본문 복사'}
                                    </button>
                                  </div>
                                </div>
                                <div className="content-manager-copy-section-content">
                                  {/* HTML 미리보기 모드 */}
                                  {PLATFORMS[selectedPlatform]?.format === 'html' && htmlPreviewMode ? (
                                    <div 
                                      className="content-manager-html-preview"
                                      dangerouslySetInnerHTML={{ 
                                        __html: extractHtmlContent(platformVersions[selectedPlatform]?.content) || '콘텐츠 없음' 
                                      }}
                                    />
                                  ) : (
                                    <pre>{extractMainContent(platformVersions[selectedPlatform]?.content) || '콘텐츠 없음'}</pre>
                                  )}
                                </div>
                              </div>

                              {/* #️⃣ 해시태그 섹션 */}
                              {extractHashtags(platformVersions[selectedPlatform]?.content) && (
                                <div className="content-manager-copy-section hashtags">
                                  <div className="content-manager-copy-section-header">
                                    <span className="content-manager-copy-section-title">#️⃣ 해시태그</span>
                                    <button 
                                      className="content-manager-copy-section-btn"
                                      onClick={() => handleCopyContent(extractHashtags(platformVersions[selectedPlatform]?.content))}
                                    >
                                      📋 해시태그 복사
                                    </button>
                                  </div>
                                  <div className="content-manager-hashtags-preview">
                                    {extractHashtags(platformVersions[selectedPlatform]?.content)}
                                  </div>
                                </div>
                              )}

                              {/* 💬 댓글용 섹션 */}
                              {extractCommentSection(platformVersions[selectedPlatform]?.content) && (
                                <div className="content-manager-copy-section comment">
                                  <div className="content-manager-copy-section-header">
                                    <span className="content-manager-copy-section-title">💬 댓글용</span>
                                    <button 
                                      className="content-manager-copy-section-btn"
                                      onClick={() => handleCopyContent(extractCommentSection(platformVersions[selectedPlatform]?.content))}
                                    >
                                      📋 댓글 복사
                                    </button>
                                  </div>
                                  <div className="content-manager-comment-preview">
                                    {extractCommentSection(platformVersions[selectedPlatform]?.content)}
                                  </div>
                                </div>
                              )}

                              {/* 전체 복사 & 발행 완료 */}
                              <div className="content-manager-content-actions bottom">
                                <button 
                                  className="content-manager-copy-btn primary large"
                                  onClick={() => handleCopyContent(platformVersions[selectedPlatform]?.content || '')}
                                >
                                  📋 전체 복사 (본문+해시태그)
                                </button>
                                <button 
                                  className="content-manager-copy-btn"
                                  onClick={() => handleMarkPublished(selectedContent?.id, selectedPlatform)}
                                >
                                  ✅ 발행 완료 표시
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* 유튜브 쇼츠 탭 */}
          {activeTab === 'shorts' && (
            <div className="content-manager-shorts">
              <div className="shorts-header">
                <h3>🐘 리액티브 캐릭터 쇼츠</h3>
                <button 
                  className="shorts-generate-btn"
                  onClick={async () => {
                    if (!window.confirm('쇼츠를 생성하시겠습니까?\n(트렌딩 토픽 리서치 → 시나리오 → 영상 생성, 약 5~10분 소요)')) return;
                    setIsLoadingShorts(true);
                    try {
                      const token = await user.getIdToken();
                      const response = await fetch(`${API_BASE_URL}/content/generate-character-shorts`, {
                        method: 'POST',
                        headers: {
                          'Authorization': `Bearer ${token}`,
                          'Content-Type': 'application/json'
                        }
                      });
                      const data = await response.json();
                      if (data.success) {
                        alert(`쇼츠 생성 완료!\n\n📌 토픽: ${data.topic?.title || 'N/A'}\n🎬 시나리오: ${data.scenario?.title || 'N/A'}\n🏷️ 타입: ${data.scenario?.type || 'N/A'}\n⏱️ 길이: ${data.duration_seconds}초`);
                        loadShortsList();
                      } else {
                        alert('생성 실패: ' + (data.detail || data.error));
                      }
                    } catch (err) {
                      alert('오류: ' + err.message);
                    }
                    setIsLoadingShorts(false);
                  }}
                  disabled={isLoadingShorts}
                >
                  {isLoadingShorts ? '생성 중... (약 5~10분)' : '🐘 테스트 쇼츠 생성'}
                </button>
              </div>
              
              <div className="shorts-info">
                <p>📌 매일 오전 7시 자동 생성 (GitHub Actions)</p>
                <p>📌 8초 코끼리 시나리오 (트렌딩 상황 x 캐릭터 반응)</p>
                <p>📌 트렌딩 토픽 리서치 → 코끼리 시나리오 → Veo 영상 → 어그로 오버레이</p>
              </div>
              
              {/* 쇼츠 목록 */}
              <div className="shorts-list">
                <h4>생성된 쇼츠 목록</h4>
                {shortsList.length === 0 ? (
                  <p className="shorts-empty">아직 생성된 쇼츠가 없습니다.</p>
                ) : (
                  shortsList.map((shorts, idx) => (
                    <div key={idx} className="shorts-item" onClick={() => setSelectedShorts(shorts)}>
                      <div className="shorts-thumbnail">
                        {shorts.thumbnail_url && <img src={shorts.thumbnail_url} alt="썸네일" />}
                        {!shorts.thumbnail_url && (
                          <div className="shorts-thumbnail-placeholder">🐘</div>
                        )}
                      </div>
                      <div className="shorts-info">
                        <h5>{shorts.title}</h5>
                        <p>
                          {shorts.duration_seconds}초 | {new Date(shorts.generated_at).toLocaleDateString('ko-KR')}
                          {shorts.type === 'character_shorts' && (
                            <span className="shorts-type-badge character">캐릭터</span>
                          )}
                          {shorts.type === 'simple_meme_shorts' && (
                            <span className="shorts-type-badge meme">밈</span>
                          )}
                        </p>
                        {shorts.topic_used && (
                          <p className="shorts-topic-label">📌 {shorts.topic_used}</p>
                        )}
                        {shorts.scenario_type && (
                          <span className="shorts-scenario-badge">{shorts.scenario_type}</span>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
              
              {/* 선택된 쇼츠 상세 - 모달 */}
              {selectedShorts && (
                <div className="shorts-detail-overlay" onClick={(e) => { if (e.target === e.currentTarget) setSelectedShorts(null); }}>
                <div className="shorts-detail-modal">
                  <button className="shorts-close-btn" onClick={() => setSelectedShorts(null)}>✕</button>
                  
                  {/* 다운로드 영역 */}
                  <div className="shorts-downloads">
                    {selectedShorts.video_url && (
                      <a href={selectedShorts.video_url} target="_blank" rel="noopener noreferrer" className="shorts-download-btn primary">
                        📥 영상 다운로드 ({selectedShorts.duration_seconds}초)
                      </a>
                    )}
                    {selectedShorts.thumbnail_url && (
                      <a href={selectedShorts.thumbnail_url} target="_blank" rel="noopener noreferrer" className="shorts-download-btn">
                        🖼️ 썸네일 다운로드
                      </a>
                    )}
                  </div>

                  {/* 캐릭터 쇼츠 전용 정보 */}
                  {selectedShorts.type === 'character_shorts' && (
                    <div className="shorts-character-info">
                      <div className="shorts-character-row">
                        <span className="label">📌 토픽:</span>
                        <span className="value">{selectedShorts.topic_used || 'N/A'}</span>
                        {selectedShorts.topic_category && (
                          <span className="shorts-scenario-badge">{selectedShorts.topic_category}</span>
                        )}
                        {selectedShorts.topic_interest_score > 0 && (
                          <span className="shorts-score">관심도 {selectedShorts.topic_interest_score}점</span>
                        )}
                      </div>
                      <div className="shorts-character-row">
                        <span className="label">🎬 시나리오:</span>
                        <span className="value">{selectedShorts.scenario_title || 'N/A'}</span>
                        {selectedShorts.scenario_type && (
                          <span className="shorts-scenario-badge">{selectedShorts.scenario_type}</span>
                        )}
                      </div>
                      {selectedShorts.story_concept && (
                        <div className="shorts-character-row">
                          <span className="label">📖 스토리:</span>
                          <span className="value">{selectedShorts.story_concept}</span>
                        </div>
                      )}
                      {selectedShorts.aggro_top_text && (
                        <div className="shorts-character-row">
                          <span className="label">🔥 어그로:</span>
                          <span className="value aggro">{selectedShorts.aggro_top_text}</span>
                          {selectedShorts.aggro_style && (
                            <span className="shorts-scenario-badge">{selectedShorts.aggro_style}</span>
                          )}
                        </div>
                      )}
                      {selectedShorts.korean_dialogue && (
                        <div className="shorts-character-row">
                          <span className="label">🗣️ 대사:</span>
                          <span className="value">{selectedShorts.korean_dialogue}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* 유튜브 제목 */}
                  <div className="shorts-copy-field">
                    <label>📌 유튜브 제목</label>
                    <div className="shorts-copy-row">
                      <input type="text" readOnly value={selectedShorts.title || ''} />
                      <button onClick={() => {
                        navigator.clipboard.writeText(selectedShorts.title || '');
                        alert('제목 복사됨!');
                      }}>복사</button>
                    </div>
                  </div>

                  {/* 유튜브 설명 */}
                  <div className="shorts-copy-field">
                    <label>📝 유튜브 설명</label>
                    <div className="shorts-copy-row">
                      <textarea readOnly value={
                        (selectedShorts.description || '') + 
                        '\n\n🐘 왓퀴즈 - 선생님을 위한 AI 퀴즈 서비스\n🔗 https://whatquiz.co.kr'
                      } rows={5} />
                      <button onClick={() => {
                        const desc = (selectedShorts.description || '') + 
                          '\n\n🐘 왓퀴즈 - 선생님을 위한 AI 퀴즈 서비스\n🔗 https://whatquiz.co.kr';
                        navigator.clipboard.writeText(desc);
                        alert('설명 복사됨!');
                      }}>복사</button>
                    </div>
                  </div>

                  {/* 해시태그 */}
                  <div className="shorts-copy-field">
                    <label>🏷️ 해시태그</label>
                    <div className="shorts-copy-row">
                      <input type="text" readOnly value={
                        selectedShorts.hashtags ||
                        selectedShorts.script?.youtube_meta?.hashtags || 
                        (selectedShorts.tags || []).map(t => t.startsWith('#') ? t : '#' + t).join(' ') + ' #Shorts'
                      } />
                      <button onClick={() => {
                        const hashtags = selectedShorts.hashtags ||
                          selectedShorts.script?.youtube_meta?.hashtags || 
                          (selectedShorts.tags || []).map(t => t.startsWith('#') ? t : '#' + t).join(' ') + ' #Shorts';
                        navigator.clipboard.writeText(hashtags);
                        alert('해시태그 복사됨!');
                      }}>복사</button>
                    </div>
                  </div>

                  {/* 고정 댓글 */}
                  <div className="shorts-copy-field">
                    <label>💬 고정 댓글 (첫 댓글로 달기)</label>
                    <div className="shorts-copy-row">
                      <textarea readOnly value={
                        selectedShorts.pinned_comment ||
                        selectedShorts.script?.youtube_meta?.pinned_comment || 
                        '이 코끼리가 다음에 뭘 할지 궁금하면 구독! 🐘💙'
                      } rows={2} />
                      <button onClick={() => {
                        const comment = selectedShorts.pinned_comment ||
                          selectedShorts.script?.youtube_meta?.pinned_comment || 
                          '이 코끼리가 다음에 뭘 할지 궁금하면 구독! 🐘💙';
                        navigator.clipboard.writeText(comment);
                        alert('고정댓글 복사됨!');
                      }}>복사</button>
                    </div>
                  </div>

                  {/* 태그 (유튜브 업로드용) */}
                  <div className="shorts-copy-field">
                    <label>🔖 태그 (쉼표 구분)</label>
                    <div className="shorts-copy-row">
                      <input type="text" readOnly value={
                        (selectedShorts.tags || []).join(', ')
                      } />
                      <button onClick={() => {
                        navigator.clipboard.writeText((selectedShorts.tags || []).join(', '));
                        alert('태그 복사됨!');
                      }}>복사</button>
                    </div>
                  </div>

                  {/* 전체 복사 버튼 */}
                  <button 
                    className="shorts-copy-all-btn"
                    onClick={() => {
                      const allText = `📌 제목:\n${selectedShorts.title}\n\n` +
                        `📝 설명:\n${selectedShorts.description}\n\n🐘 왓퀴즈 → https://whatquiz.co.kr\n\n` +
                        `🏷️ 해시태그:\n${selectedShorts.hashtags || selectedShorts.script?.youtube_meta?.hashtags || (selectedShorts.tags || []).map(t => '#' + t).join(' ')}\n\n` +
                        `💬 고정댓글:\n${selectedShorts.pinned_comment || selectedShorts.script?.youtube_meta?.pinned_comment || '이 코끼리가 다음에 뭘 할지 궁금하면 구독! 🐘'}`;
                      navigator.clipboard.writeText(allText);
                      alert('전체 정보 복사됨!');
                    }}
                  >
                    📋 전체 정보 복사하기
                  </button>

                  {/* 시나리오/스크립트 상세 (접기 가능) */}
                  <details className="shorts-script-detail">
                    <summary>📜 시나리오 상세 보기</summary>
                    <div className="shorts-script-content">
                      {/* 캐릭터 쇼츠: 시나리오 기반 */}
                      {selectedShorts.type === 'character_shorts' && selectedShorts.scenario && (
                        <>
                          <div className="script-section">
                            <strong>🎯 Story Concept</strong>
                            <p>{selectedShorts.scenario.story_concept || 'N/A'}</p>
                          </div>
                          <div className="script-section">
                            <strong>🎬 Video Prompt</strong>
                            <p style={{fontSize: '12px', color: '#666', whiteSpace: 'pre-wrap'}}>{selectedShorts.scenario.video_prompt || 'N/A'}</p>
                          </div>
                          <div className="script-section">
                            <strong>🔊 Audio Prompt</strong>
                            <p style={{fontSize: '12px', color: '#666', whiteSpace: 'pre-wrap'}}>{selectedShorts.scenario.audio_prompt || 'N/A'}</p>
                          </div>
                          <div className="script-section">
                            <strong>🔄 Loop Design</strong>
                            <p>{selectedShorts.scenario.loop_design || selectedShorts.loop_design || 'N/A'}</p>
                          </div>
                        </>
                      )}
                      {/* 레거시 밈 쇼츠: 기존 스크립트 구조 */}
                      {selectedShorts.type !== 'character_shorts' && (
                        <>
                          {selectedShorts.script?.hook && (
                            <div className="script-section">
                              <strong>🎣 훅 ({selectedShorts.script.hook.duration_sec}초)</strong>
                              <p>{selectedShorts.script.hook.text}</p>
                            </div>
                          )}
                          {selectedShorts.script?.scenes?.map((scene, i) => (
                            <div key={i} className="script-section">
                              <strong>🎬 씬 {scene.index} ({scene.duration_sec}초)</strong>
                              <p>{scene.narration}</p>
                            </div>
                          ))}
                          {selectedShorts.script?.cta && (
                            <div className="script-section">
                              <strong>📢 CTA ({selectedShorts.script.cta.duration_sec}초)</strong>
                              <p>{selectedShorts.script.cta.text}</p>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </details>
                </div>
                </div>
              )}
            </div>
          )}

          {/* 설정 탭 */}
          {activeTab === 'settings' && (
            <div className="content-manager-settings">
              <h3>자동화 설정</h3>
              <div className="content-manager-settings-info">
                <p>토픽 DB: {contentTopics.length}개 주제</p>
                <p>카테고리: {Object.keys(topicsByCategory).join(', ')}</p>
              </div>
              
              <div className="content-manager-settings-note">
                <h4>스케줄러 설정 (서버)</h4>
                <p>매일 08:00 자동 실행을 위해서는 GitHub Actions 또는 Cloud Scheduler 설정이 필요합니다.</p>
                <code>POST /content/generate-daily</code>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContentManager;
